import { Bar, XAxis, YAxis, CartesianGrid, Tooltip as RechartsTooltip, ComposedChart } from 'recharts';
import { AnimalTrendDataPoint } from '../../models/animal-trend-data';
import { ChartContainer, ChartLegend, ChartLegendContent, type ChartConfig } from '../../elements/ui/chart';

const FALLBACK_COLORS = ['#8884d8', '#82ca9d', '#ffc658', '#ff7300', '#a4de6c'];

export interface TrendValueChartProps {
  trendData: {
    name: string;
    color?: string;
    data: AnimalTrendDataPoint[];
  }[];
  showPercentage?: boolean;
}

function ValueTooltip({ active, payload, label, showPercentage, grandTotal }: any) {
  if (!active || !payload?.length) return null;
  return (
    <div className="rounded-lg border border-gray-200 bg-white px-3 py-2 shadow-md text-xs">
      <p className="font-medium text-gray-700 mb-1">{label}</p>
      {payload.map((entry: any) => (
        <div key={entry.name} className="flex items-center gap-1.5">
          <div className="w-2 h-2 rounded-full" style={{ backgroundColor: entry.fill || entry.color }} />
          <span className="text-gray-600">{entry.name}:</span>
          <span className="font-medium text-gray-900">{entry.value}</span>
          {showPercentage && grandTotal > 0 && (
            <span className="text-gray-500">({((entry.value / grandTotal) * 100).toFixed(1)}%)</span>
          )}
        </div>
      ))}
    </div>
  );
}

export default function TrendValueChart({ trendData, showPercentage }: TrendValueChartProps) {
  const grandTotal = trendData.reduce((sum, set) => sum + set.data.reduce((s, d) => s + (d.animalCount ?? 0), 0), 0);
  const allXValues = Array.from(new Set(trendData.flatMap((set) => set.data.map((item) => item.value))));

  const chartData = allXValues.map((x) => {
    const entry: Record<string, string | number | null | undefined> = { value: x };
    trendData.forEach((set, i) => {
      const match = set.data.find((d) => d.value === x);
      entry[`series_${i}`] = match?.animalCount ?? 0;
    });
    return entry;
  });

  const chartConfig = trendData.reduce<ChartConfig>((acc, set, i) => {
    acc[`series_${i}`] = {
      label: set.name,
      color: set.color ?? FALLBACK_COLORS[i % FALLBACK_COLORS.length]
    };
    return acc;
  }, {});

  return (
    <ChartContainer config={chartConfig} className="w-full h-full">
      <ComposedChart data={chartData} margin={{ top: 10, right: 20, bottom: 20, left: 20 }}>
        <CartesianGrid vertical={false} />
        <XAxis
          dataKey="value"
          padding={{ left: 30, right: 30 }}
          scale="band"
          tickLine={false}
          axisLine={false}
          tickMargin={10}
        />
        <YAxis axisLine={false} tickLine={false} width={40} allowDecimals={false} />
        <RechartsTooltip
          cursor={false}
          content={<ValueTooltip showPercentage={showPercentage} grandTotal={grandTotal} />}
        />
        <ChartLegend content={<ChartLegendContent />} />
        {trendData.map((set, i) => (
          <Bar
            key={i}
            name={set.name}
            dataKey={`series_${i}`}
            fill={`var(--color-series_${i})`}
            radius={[4, 4, 0, 0]}
          />
        ))}
      </ComposedChart>
    </ChartContainer>
  );
}
